import { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, PawPrint, Clock, DollarSign, Package, FileText,
  MessageCircle, Pin, Calendar, CalendarDays, Folder, Sparkles, Settings, Shield,
  User, ClipboardList, Inbox, Heart, AlertTriangle, HandHeart,
} from 'lucide-react'
import { classNames } from '../../lib/utils'
import { supabase } from '../../lib/supabase'
import { useRole } from '../../hooks/useRole'
import { useModules } from '../../hooks/useModules'
import { useOrg } from '../../hooks/useOrg'

// Each item declares which roles may see it. Foster role is locked
// down to: Home, My Animals, Chat, Support, My Profile.
const ALL_ITEMS = [
  // Foster: Home label is "Home". Staff: Dashboard. Same route.
  { to: '/',           icon: LayoutDashboard, label: 'Dashboard',   fosterLabel: 'Home',       end: true, module: null,         roles: ['admin','member','foster','super_admin'] },
  { to: '/animals',    icon: PawPrint,        label: 'Animals',     fosterLabel: 'My Animals',            module: 'animals',    roles: ['admin','member','foster','super_admin'] },
  { to: '/hours',      icon: Clock,           label: 'Hours',                                              module: 'hours',      roles: ['admin','member','super_admin'] },
  { to: '/finances',   icon: DollarSign,      label: 'Finances',                                           module: 'finances',   roles: ['admin','member','super_admin'] },
  { to: '/inventory',  icon: Package,         label: 'Inventory',                                          module: 'inventory',  roles: ['admin','member','super_admin'] },
  { to: '/agreements', icon: FileText,        label: 'Agreements',                                         module: 'agreements', roles: ['admin','member','super_admin'] },
  { to: '/applications', icon: ClipboardList, label: 'Applications',                                       module: null,         roles: ['admin','member','super_admin'] },
  { to: '/chat',       icon: MessageCircle,   label: 'Chat',                                               module: null,         roles: ['admin','member','foster','super_admin'], badge: 'chat' },
  { to: '/inbox',      icon: Inbox,           label: 'Inbox',                                              module: null,         roles: ['admin','member','super_admin'] },
  { to: '/board',      icon: Pin,             label: 'Board',                                              module: null,         roles: ['admin','member','super_admin'] },
  { to: '/calendar',   icon: CalendarDays,    label: 'Calendar',                                           module: null,         roles: ['admin','member','super_admin'] },
  { to: '/documents',  icon: Folder,          label: 'Documents',                                          module: null,         roles: ['admin','member','super_admin'] },
  { to: '/donors',     icon: Heart,           label: 'Donors',                                             module: 'donors',     roles: ['admin','super_admin'] },
  { to: '/adopters',   icon: HandHeart,       label: 'Adopters',                                           module: null,         roles: ['admin','member','super_admin'] },
  { to: '/support',    icon: Sparkles,        label: 'Support',                                            module: null,         roles: ['foster','admin','super_admin'] },
  { to: '/safety-alerts', icon: AlertTriangle, label: 'Safety Alerts',                                      module: null,         roles: ['admin','super_admin'], badge: 'safety' },
  { to: '/settings',   icon: Settings,        label: 'Settings',                                           module: null,         roles: ['admin','member','super_admin'] },
  { to: '/platform',   icon: Shield,          label: 'Platform',                                           module: null,         roles: ['super_admin'] },
  { to: '/me',         icon: User,            label: 'My Profile',                                         module: null,         roles: ['foster'] },
]

export default function Sidebar() {
  const { role, isFoster, isAdmin } = useRole()
  const { isEnabled } = useModules()
  const { orgId } = useOrg()
  const [safetyCount, setSafetyCount] = useState(0)
  const [chatUnread, setChatUnread] = useState(0)

  useEffect(() => {
    if (!orgId || !isAdmin) return
    supabase
      .from('safety_alerts')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('status', 'new')
      .then(({ count }) => setSafetyCount(count || 0))
  }, [orgId, isAdmin])

  // Total unread chat count via single RPC
  useEffect(() => {
    if (!orgId) return
    ;(async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return
      const { data } = await supabase.rpc('get_unread_counts', { p_user_id: authUser.id })
      const total = (data || []).reduce((s, r) => s + (r.unread_count || 0), 0)
      setChatUnread(total)
    })()
  }, [orgId])

  const visible = ALL_ITEMS.filter(item =>
    item.roles.includes(role) && (!item.module || isEnabled(item.module))
  )

  return (
    <aside className="hidden lg:flex flex-col w-64 flex-shrink-0 bg-surface-container-lowest overflow-y-auto" style={{ border: 'none' }}>
      <div className="px-6 pt-7 pb-6">
        <img src="/placed-logo.svg" alt="Placed" style={{ height: 42, width: 'auto', display: 'block' }} />
      </div>

      <nav className="flex-1 px-3 pb-6 space-y-1">
        {visible.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => classNames(
              'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
              isActive ? 'bg-primary text-on-primary' : 'text-on-surface hover:bg-surface-container-high'
            )}
          >
            {({ isActive }) => (
              <>
                <item.icon size={18} strokeWidth={1.5} className={isActive ? 'text-on-primary' : 'text-outline'} />
                <span className="flex-1">{isFoster && item.fosterLabel ? item.fosterLabel : item.label}</span>
                {item.badge === 'safety' && safetyCount > 0 && (
                  <span className="min-w-[18px] h-[18px] rounded-full text-[0.5625rem] font-bold flex items-center justify-center flex-shrink-0"
                    style={{ background: '#ba1a1a', color: '#ffffff' }}
                  >
                    {safetyCount > 99 ? '99+' : safetyCount}
                  </span>
                )}
                {item.badge === 'chat' && chatUnread > 0 && (
                  <span className="min-w-[18px] h-[18px] rounded-full bg-primary text-on-primary text-[0.5625rem] font-bold flex items-center justify-center flex-shrink-0">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  )
}
